// Crash reporting seam.
//
// The app currently has NO error-reporting backend: a crash is visible only if
// the user happens to open their console and tells someone. That is the gap
// between "recovers gracefully" and "we know it happened".
//
// This is the single choke point every crash flows through, so wiring a real
// service later (Sentry, Datadog RUM, an internal endpoint) is a change to THIS
// file only — no hunting through components. Until then it logs to console.error,
// which survives the production console.log stub in index.js.
//
// Deliberately dependency-free: adding a paid/third-party SDK is a decision for
// whoever owns the account, not something to smuggle in behind a bug fix.

import { isChunkLoadError, reloadForNewBuild } from "./chunkReload";

let reporter = null;

/**
 * Install the real reporter, e.g. from index.js:
 *   setErrorReporter((error, context) => Sentry.captureException(error, { extra: context }));
 */
export const setErrorReporter = (fn) => {
  reporter = typeof fn === "function" ? fn : null;
};

/**
 * Report a crash. Never throws — a failure inside error reporting must not
 * become a second error on top of the first.
 */
export const reportError = (error, context = {}) => {
  const payload = {
    ...context,
    message: error?.message || String(error),
    stack: error?.stack,
    url: typeof window !== "undefined" ? window.location?.href : undefined,
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  };

  console.error("[reportError]", error, payload);

  if (!reporter) return;
  try {
    reporter(error, payload);
  } catch (e) {
    console.error("[reportError] the reporter itself failed:", e);
  }
};

/**
 * A failed HTTP call is an EXPECTED condition in this app — the API layer already
 * surfaces it as a toast — and offline users generate them constantly. Without
 * this filter, wiring a crash service would drown real defects in network noise
 * and bill for every one. Programming errors (TypeError, SyntaxError, …) are
 * never filtered.
 */
const isExpectedNetworkFailure = (reason) => {
  if (!reason || typeof reason !== "object") return false;
  if (reason.isAxiosError || reason.response || reason.request) return true;
  const msg = String(reason.message || "");
  return (
    /Network Error/i.test(msg) ||
    /timeout of \d+ms exceeded/i.test(msg) ||
    /Request failed with status code/i.test(msg) ||
    /Failed to fetch/i.test(msg)
  );
};

/**
 * Catch what an error boundary structurally cannot: unhandled promise
 * rejections and errors thrown outside React (event handlers, timers, module
 * evaluation). Call once at startup.
 */
let globalHandlersInstalled = false;

export const installGlobalErrorHandlers = () => {
  if (typeof window === "undefined") return;
  // Idempotent: a second call (HMR, a second entry point, a test re-running
  // setup) would otherwise stack listeners and report every error N times.
  if (globalHandlersInstalled) return;
  globalHandlersInstalled = true;

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    // A dynamic import that failed outside React's render path — a prefetch, a
    // click handler, a preloaded route — never reaches the error boundary. It
    // means the same thing there as it does inside it: a newer build replaced
    // the files this bundle is asking for.
    if (isChunkLoadError(reason) && reloadForNewBuild()) return;
    if (isExpectedNetworkFailure(reason)) {
      console.warn("[network] unhandled API rejection:", reason?.message || reason);
      return;
    }
    reportError(reason, { kind: "unhandledrejection" });
  });

  window.addEventListener("error", (event) => {
    // Resource load failures (img/script) also fire this with no `error`.
    if (!event?.error) return;
    if (isChunkLoadError(event.error) && reloadForNewBuild()) return;
    reportError(event.error, { kind: "window.onerror" });
  });
};
